import { useState } from "react";
import axios from "axios";
import { Loader2, ShoppingCart, AlertCircle, MessageSquarePlus } from "lucide-react";
import { trackEvent, trackMetaEvent } from "@/components/Analytics";
import { CheckoutDetailsDialog } from "@/components/CheckoutDetailsDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// productPath must match the /{category}/{slug} route used in the backend
// product_catalog.json (e.g. "/roof-tents/xpeditionz-sub60").
export const BuyNowButton = ({ productPath, testId = "buy-now-button", label = "Buy Now", selectedOptions = null, disabled = false, disabledHint = "Select all options below to continue" }) => {
  const [state, setState] = useState({ loading: false, error: null });
  const [showNotes, setShowNotes] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const [showDetails, setShowDetails] = useState(false);

  const handleClick = () => {
    trackEvent("buy_now_click");
    // No separate "cart" step on this site — Buy Now goes straight to the
    // details step then Stripe, so both events fire together (matches
    // Meta's guidance for single-click "buy it now" flows), which also
    // feeds the Admin Panel's Abandoned Carts report for anyone who starts
    // but doesn't finish paying.
    trackMetaEvent("AddToCart", { content_ids: [productPath], content_type: "product" });
    trackMetaEvent("InitiateCheckout", { content_ids: [productPath], content_type: "product", num_items: 1 });
    setState({ loading: false, error: null });
    setShowDetails(true);
  };

  const handleDetailsSubmit = async (customer) => {
    setState({ loading: true, error: null });
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        product_path: productPath,
        quantity: 1,
        origin_url: window.location.origin,
        selected_options: selectedOptions || undefined,
        order_notes: orderNotes.trim() || undefined,
        customer,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start checkout. Please try again or enquire below.";
      setState({ loading: false, error: detail });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={state.loading || disabled}
        data-testid={testId}
        className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {state.loading ? (
          <><Loader2 size={14} className="animate-spin" /> Redirecting…</>
        ) : (
          <><ShoppingCart size={14} /> {label}</>
        )}
      </button>
      {disabled && !state.loading && disabledHint && (
        <p className="text-xs text-[var(--text-secondary)]">{disabledHint}</p>
      )}
      {!showNotes ? (
        <button
          type="button"
          onClick={() => setShowNotes(true)}
          data-testid={`${testId}-add-notes`}
          className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-colors self-start"
        >
          <MessageSquarePlus size={13} /> Add a note for your order (optional)
        </button>
      ) : (
        <textarea
          value={orderNotes}
          onChange={(e) => setOrderNotes(e.target.value)}
          placeholder="e.g. preferred colour reminder, install date, delivery instructions…"
          maxLength={500}
          rows={2}
          data-testid={`${testId}-order-notes`}
          className="w-full max-w-xs text-sm bg-white/5 border border-white/15 rounded-md px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-[var(--brand-primary)]/70"
        />
      )}
      {state.error && (
        <p data-testid={`${testId}-error`} className="flex items-center gap-2 text-xs text-red-400 max-w-xs">
          <AlertCircle size={14} className="shrink-0" /> {state.error}
        </p>
      )}
      <CheckoutDetailsDialog
        open={showDetails}
        onOpenChange={setShowDetails}
        onSubmit={handleDetailsSubmit}
        submitting={state.loading}
        error={state.error}
      />
    </div>
  );
};
